/** Where the Rally server lives. Same origin by default; any Rally deployment
 * works — a Vercel URL, a local `pnpm start`, or an AgentCore build.
 *
 * Resolution order (first non-empty wins):
 *   1. `?server=<url>` query parameter (also remembered for next visits)
 *   2. `localStorage['rally-server-url']` (set by the Connect screen)
 *   3. `window.RALLY_SERVER_URL` (injected by config.js, e.g. on the HF Space)
 *   4. '' — same origin, the way a self-hosted deployment serves it.
 */

const KEY = 'rally-server-url';

const param = new URLSearchParams(window.location.search).get('server')?.trim() || '';
if (param) localStorage.setItem(KEY, param.replace(/\/+$/, ''));

const stored = localStorage.getItem(KEY) ?? '';
const injected = (window as { RALLY_SERVER_URL?: string }).RALLY_SERVER_URL ?? '';

export const SERVER_URL: string = (param || stored || injected).replace(/\/+$/, '');
export const hasServer = (): boolean => SERVER_URL.length > 0;

/** Prefix a server path with the configured base URL ('' keeps it same-origin). */
export const srv = (path: string): string => SERVER_URL + path;

/** Save the server URL and reload without the ?server parameter (it would override). */
export const setServerUrl = (url: string): void => {
  localStorage.setItem(KEY, url.trim().replace(/\/+$/, ''));
  const next = new URL(window.location.href);
  next.searchParams.delete('server');
  window.location.assign(next.toString().replace(/#.*$/, '') + window.location.hash);
};

/** Forget the saved server and reload (back to same-origin mode). */
export const clearServerUrl = (): void => {
  localStorage.removeItem(KEY);
  const next = new URL(window.location.href);
  next.searchParams.delete('server');
  window.location.reload();
};

/** Reachability probe against the configured server. */
export async function probeServer(timeoutMs = 4000): Promise<{ ok: boolean; detail?: string }> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(srv('/api/health'), { signal: ctrl.signal });
    clearTimeout(timer);
    if (res.ok) return { ok: true };
    return { ok: false, detail: `HTTP ${res.status}` };
  } catch (err) {
    return { ok: false, detail: (err as Error).name === 'AbortError' ? 'timed out' : 'unreachable' };
  }
}
